import { compareAsc } from 'date-fns';
import { createSelector } from 'reselect';
import { selectFeatureFlag } from 'reducers/selectors';
export const selectIncludeIncomeSubscriptions = (store) => selectFeatureFlag(store, 'recurring_income').value;
export const selectSubscriptions = (store) => store.subscriptions.subscriptions.data;
export const selectActiveSubscriptions = createSelector([selectSubscriptions], (subscriptions) => subscriptions.filter(({ isActive }) => isActive));
export const selectOrderedActiveSubscriptions = createSelector([selectActiveSubscriptions], (subscriptions) => subscriptions.slice().sort((a, b) => {
    if (a && a.predictions && !a.predictions.length) {
        return 1;
    }
    if (b && b.predictions && !b.predictions.length) {
        return -1;
    }
    const pa = a.predictions[0];
    const pb = b.predictions[0];
    return compareAsc(new Date(pa.date), new Date(pb.date));
}));
const selectIncomeParam = (_, income) => income;
export const selectInactiveSubscriptions = createSelector([selectSubscriptions, selectIncomeParam], (subscriptions, income) => subscriptions.filter(({ isActive, isIncome }) => !isActive && (income === undefined || Boolean(isIncome) === income)));
export const selectInactiveSubscriptionsLength = createSelector([selectInactiveSubscriptions], (subscriptions) => subscriptions.length);
export const makeSelectSubscriptionById = () => createSelector([
    selectSubscriptions,
    (_state, subscriptionId) => subscriptionId,
], (subscriptions, subscriptionId) => subscriptions.find((subscription) => subscription.id === subscriptionId));
const byMostRecentCharge = (a, b) => (b.lastChargeDate ?? '').localeCompare(a.lastChargeDate ?? '');
export const selectDuplicateGroups = createSelector([selectSubscriptions], (subscriptions) => {
    const eligible = subscriptions.filter(({ isActive, isIncome }) => isActive && !isIncome);
    const eligibleById = new Map(eligible.map((sub) => [sub.id, sub]));
    const grouped = new Set();
    const groups = [];
    eligible.forEach((start) => {
        if (grouped.has(start.id) || !start.duplicateOf?.length)
            return;
        const memberIds = new Set();
        const pending = [start.id];
        while (pending.length) {
            const id = pending.pop();
            const sub = eligibleById.get(id);
            if (sub && !memberIds.has(id)) {
                memberIds.add(id);
                pending.push(...(sub.duplicateOf ?? []));
            }
        }
        memberIds.forEach((id) => grouped.add(id));
        if (memberIds.size > 1) {
            groups.push(eligible
                .filter((sub) => memberIds.has(sub.id))
                .sort(byMostRecentCharge));
        }
    });
    return groups;
});
export const selectHasDuplicateGroups = (store) => selectDuplicateGroups(store).length > 0;
/**
 * The group `subscriptionId` belongs to, with that subscription first and the
 * rest most recently charged first. The review is opened with the group's most
 * recent member from the list, and with the subscription being viewed from its
 * detail screen, so each lists the one it started from first.
 */
export const makeSelectDuplicateGroupBySubscriptionId = () => createSelector([
    selectDuplicateGroups,
    (_state, subscriptionId) => subscriptionId,
], (groups, subscriptionId) => {
    const group = groups.find((members) => members.some(({ id }) => id === subscriptionId));
    if (!group)
        return undefined;
    const anchor = group.find(({ id }) => id === subscriptionId);
    return [anchor, ...group.filter((member) => member !== anchor)];
});
export const selectHasActiveSubscriptionsForAccount = createSelector([selectActiveSubscriptions, (_, accountId) => accountId], (activeSubscriptions, accountId) => {
    const subscriptions = activeSubscriptions.filter((subscription) => subscription.accountId === accountId);
    return subscriptions.length > 0;
});
